import config from "../../../config";
import swal from "sweetalert";
import axiosInstance from "../../../services/AxiosInstance";
import formatError from "../../../services/FormatError";
import {
  EVENT_CONFIG_FETCH_REQUEST,
  EVENT_CONFIG_FETCH_SUCCESS,
  EVENT_CONFIG_FETCH_FAILURE,
  EVENT_CONFIG_UPDATE_REQUEST,
  EVENT_CONFIG_UPDATE_SUCCESS,
  EVENT_CONFIG_UPDATE_FAILURE,
  EVENT_CONFIG_TOGGLE_CHANNEL,
  EVENT_CONFIG_CLEAR_ERROR,
} from "./eventConfigTypes";
import { showSuccessNotification } from "../ui/uiActions";

// Helper function to transform API data to your existing structure
const transformApiDataToEventConfigs = (apiData) => {
  // Group by event_type
  const groupedByEventType = apiData.reduce((acc, item) => {
    if (!acc[item.event_type]) {
      acc[item.event_type] = [];
    }
    acc[item.event_type].push(item);
    return acc;
  }, {});

  // Transform each group into your existing structure
  return Object.entries(groupedByEventType).map(([eventType, configs]) => {
    const eventConfig = {
      id: eventType, // Use event_type as the main ID
      eventType: eventType,
      sms: {
        id: null,
        enabled: false,
      },
      email: {
        id: null,
        enabled: false,
      },
      whatsapp: {
        id: null,
        enabled: false,
      },
      telegram: {
        id: null,
        enabled: false,
      },
      updatedAt: null,
    };

    // Set channel states based on API data
    configs.forEach((config) => {
      if (config.media === "sms") {
        eventConfig.sms = { id: config.id, enabled: config.enabled };
      } else if (config.media === "email") {
        eventConfig.email = { id: config.id, enabled: config.enabled };
      } else if (config.media === "whatsapp") {
        eventConfig.whatsapp = { id: config.id, enabled: config.enabled };
      } else if (config.media === "telegram") {
        eventConfig.telegram = { id: config.id, enabled: config.enabled };
      }

      // Use the most recent created_at as updatedAt
      if (!eventConfig.updatedAt || config.created_at > eventConfig.updatedAt) {
        eventConfig.updatedAt = config.created_at.split("T")[0]; // Extract date part
      }
    });

    return eventConfig;
  });
};

// Helper function to transform your structure back to API format for updates
const transformConfigToApiUpdates = (configId, channel, enabled) => {
  return {
    event_type: configId,
    media: channel,
    enabled: enabled,
  };
};
const getChannelUpdateData = (enabled) => {
  return { enabled };
};
// Action Creators
export const fetchEventConfigRequest = () => ({
  type: EVENT_CONFIG_FETCH_REQUEST,
});

export const fetchEventConfigSuccess = (configs) => ({
  type: EVENT_CONFIG_FETCH_SUCCESS,
  payload: configs,
});

export const fetchEventConfigFailure = (error) => ({
  type: EVENT_CONFIG_FETCH_FAILURE,
  payload: error,
});

export const updateEventConfigRequest = () => ({
  type: EVENT_CONFIG_UPDATE_REQUEST,
});

export const updateEventConfigSuccess = (config) => ({
  type: EVENT_CONFIG_UPDATE_SUCCESS,
  payload: config,
});

export const updateEventConfigFailure = (error) => ({
  type: EVENT_CONFIG_UPDATE_FAILURE,
  payload: error,
});

export const toggleChannel = (configId, channel) => ({
  type: EVENT_CONFIG_TOGGLE_CHANNEL,
  payload: { configId, channel },
});

export const clearEventConfigError = () => ({
  type: EVENT_CONFIG_CLEAR_ERROR,
});

// Thunk Actions
export const fetchEventConfigs = () => {
  return async (dispatch) => {
    dispatch(fetchEventConfigRequest());

    try {
      const token = JSON.parse(sessionStorage.getItem("userDetails"))?.result
        ?.token;

      const response = await axiosInstance({
        url: config.routes.eventConfigsApi, // Replace with your actual route
        method: "get",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then((response) => {
          console.log("Event Configs Response:", response.data);
          return response;
        })
        .catch((error) => {
          console.log("Error Response:", error.response);
          formatError(error.response?.data?.error);
          throw error;
        });

      // Transform API data to your existing structure
      const transformedConfigs = transformApiDataToEventConfigs(
        response.data.result
      );
      dispatch(fetchEventConfigSuccess(transformedConfigs));
    } catch (error) {
      const apiError = {
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch event configurations",
        code: "FETCH_ERROR",
      };
      dispatch(fetchEventConfigFailure(apiError));
    }
  };
};

export const updateEventConfig = (configId, updates) => {
  console.log("Update Config:", configId, updates);

  return async (dispatch) => {
    dispatch(updateEventConfigRequest());

    try {
      const token = JSON.parse(sessionStorage.getItem("userDetails"))?.result
        ?.token;

      // Convert updates to API format
      const apiUpdates = [];
      Object.entries(updates).forEach(([channel, enabled]) => {
        if (["sms", "email", "whatsapp", "telegram"].includes(channel)) {
          apiUpdates.push(
            transformConfigToApiUpdates(configId, channel, enabled)
          );
        }
      });

      // Send updates to API (assuming bulk update or individual updates)
      const updatePromises = apiUpdates.map((update) =>
        axiosInstance({
          url: config.routes.updateEventConfigApi(configId), // Replace with your actual route
          method: "put", // or "patch" depending on your API
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: update,
        })
          .then((response) => {
            console.log("Update Response:", response.data);
            return response;
          })
          .catch((error) => {
            console.log("Update Error Response:", error.response);
            formatError(error.response?.data?.error);
            throw error;
          })
      );

      await Promise.all(updatePromises);

      const updatedConfig = {
        ...updates,
        id: configId,
        eventType: configId,
        updatedAt: new Date().toISOString().split("T")[0],
      };

      dispatch(updateEventConfigSuccess(updatedConfig));
      swal("Success", "Event configuration updated successfully", "success");
    } catch (error) {
      const apiError = {
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to update event configuration",
        code: "UPDATE_ERROR",
      };
      dispatch(updateEventConfigFailure(apiError));
    }
  };
};

// Alternative thunk for toggling a single channel
export const toggleEventConfigChannel = (
  configId,
  channel,
  mediaId,
  enabled
) => {
  return async (dispatch, getState) => {
    dispatch(updateEventConfigRequest());

    try {
      const token = JSON.parse(sessionStorage.getItem("userDetails"))?.result
        ?.token;

      const response = await axiosInstance({
        url: config.routes.updateEventConfigApi(mediaId),
        method: "put",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          enabled: !enabled,
        },
      })
        .then((response) => {
          dispatch(
            showSuccessNotification(`${channel.toUpperCase()} channel updated`)
          );
          return response;
        })
        .catch((error) => {
          console.log("Toggle Error Response:", error.response);

          formatError(error.response?.data?.error);
          throw error;
        });

      const { eventConfig } = getState();

      const currentConfig = eventConfig.configs.find(
        (config) => config.id === configId
      );
      console.log("currentConfig", currentConfig);
      if (!currentConfig) {
        throw new Error("Configuration not found");
      }

      const newEnabled = !currentConfig[channel].enabled;

      // Dispatch the toggle action for local state update
      dispatch(toggleChannel(configId, channel));

      const updatedConfig = {
        ...currentConfig,
        [channel]: {
          ...currentConfig[channel],
          enabled: newEnabled,
        },
        updatedAt: new Date().toISOString().split("T")[0],
      };

      dispatch(updateEventConfigSuccess(updatedConfig));
    } catch (error) {
      const apiError = {
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to toggle channel",
        code: "TOGGLE_ERROR",
      };
      dispatch(updateEventConfigFailure(apiError));
    }
  };
};
